import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProfileCard from "@/components/ProfileCard";
import { api, fileUrl, avatarUrl, formatApiError } from "@/lib/api";
import { Heart, Eye, MessageCircle, Bookmark, Camera, Sparkles, BadgeCheck, Crown, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [recommended, setRecommended] = useState([]);
  const [received, setReceived] = useState([]);
  const [views, setViews] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [rec, rcv, vw, sl] = await Promise.all([
        api.get("/matches/recommended?limit=8"),
        api.get("/interests/received"),
        api.get("/profile/views"),
        api.get("/shortlist"),
      ]);
      setRecommended(rec.data.results || []);
      setReceived(rcv.data.results || []);
      setViews(vw.data.results || []);
      setShortlist(sl.data.results || []);
    } catch (e) { console.error(e); }
  };

  const sendInterest = async (id) => {
    try {
      await api.post("/interests/send", { to_user_id: id });
      toast.success("Interest sent");
    } catch (e) {
      const status = e.response?.status;
      const msg = formatApiError(e.response?.data?.detail);
      if (status === 402) {
        toast.error(msg || "Upgrade to send interests", {
          action: { label: "View plans", onClick: () => window.location.assign("/plans") },
        });
      } else {
        toast.error(msg);
      }
    }
  };

  const toggleShortlist = async (id) => {
    const exists = shortlist.find((s) => s.user_id === id);
    try {
      if (exists) { await api.delete(`/shortlist/${id}`); toast.success("Removed from shortlist"); }
      else { await api.post(`/shortlist/${id}`); toast.success("Added to shortlist"); }
      const sl = await api.get("/shortlist");
      setShortlist(sl.data.results || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const respond = async (interestId, action) => {
    try {
      await api.post("/interests/respond", { interest_id: interestId, action });
      toast.success(action === "accept" ? "Interest accepted" : "Interest declined");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user) return null;
  const completion = user.profile_completion ?? 0;

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline">Welcome back</p>
            <h1 className="font-serif text-4xl text-brand-ink mt-2">Hello, {user.name?.split(" ")[0]}</h1>
          </div>
          {user.plan === "free" ? (
            <Link to="/plans" className="btn-gold hidden md:flex items-center gap-2"><Crown className="h-4 w-4" /> Upgrade</Link>
          ) : (
            <span className="hidden md:inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/40 text-brand-goldDark px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-semibold"><BadgeCheck className="h-3 w-3" /> {user.plan} member</span>
          )}
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          {/* Sidebar: Profile */}
          <aside className="md:col-span-3 space-y-5">
            <div className="card-elegant p-5">
              <div className="relative w-32 h-32 mx-auto rounded-full overflow-hidden bg-brand-muted border-2 border-brand-gold/30">
                {user.photo_url ? (
                  <img src={fileUrl(user.photo_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-brand-inkMute text-xs">No photo</div>
                )}
                <button onClick={() => fileRef.current?.click()} className="absolute bottom-1 right-1 bg-brand-maroon text-white p-2 rounded-full hover:bg-brand-maroonDark" data-testid="dashboard-upload-photo">
                  <Camera className="h-3 w-3" />
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoChange} />
              </div>
              <h3 className="font-serif text-xl text-center text-brand-ink mt-4">{user.name}</h3>
              <p className="text-center text-xs text-brand-inkLight">{user.profession || "Add profession"}</p>
              {uploading && <p className="text-xs text-brand-gold text-center mt-2">Uploading...</p>}

              <div className="mt-5">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="overline">Profile</span>
                  <span className="text-brand-maroon font-medium">{completion}%</span>
                </div>
                <div className="h-1.5 bg-brand-muted rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-brand-maroon to-brand-gold transition-all" style={{ width: `${completion}%` }} />
                </div>
                {completion < 100 && (
                  <Link to="/profile" className="block mt-3 text-center text-xs text-brand-maroon hover:underline">Complete your profile →</Link>
                )}
              </div>
            </div>

            {(user.plan === "free" || (user.interest_credits ?? 0) > 0) && user.role !== "admin" && (
              <div className="card-elegant p-5" data-testid="dashboard-credits-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-brand-gold" />
                    <span className="overline">Interest credits</span>
                  </div>
                  <span className="font-serif text-3xl text-brand-maroon" data-testid="credits-count">{user.interest_credits ?? 0}</span>
                </div>
                <p className="text-xs text-brand-inkLight mt-2 leading-relaxed">
                  {(user.interest_credits ?? 0) > 0
                    ? "One credit unlocks one interest send."
                    : "You're out of credits. Buy a pack or upgrade for unlimited interests."}
                </p>
                <Link to="/plans#credit-packs-section" className="block mt-3 text-center text-xs text-brand-maroon hover:underline font-medium" data-testid="dashboard-buy-credits">
                  {(user.interest_credits ?? 0) > 0 ? "Buy more credits →" : "Buy credits →"}
                </Link>
              </div>
            )}

            <div className="card-elegant p-5">
              <p className="overline mb-3">Quick stats</p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center justify-between"><span className="flex items-center gap-2 text-brand-ink"><Eye className="h-4 w-4 text-brand-gold" /> Profile views</span> <strong>{views.length}</strong></li>
                <li className="flex items-center justify-between"><span className="flex items-center gap-2 text-brand-ink"><Heart className="h-4 w-4 text-brand-gold" /> Interests received</span> <strong>{received.length}</strong></li>
                <li className="flex items-center justify-between"><span className="flex items-center gap-2 text-brand-ink"><Bookmark className="h-4 w-4 text-brand-gold" /> Shortlisted</span> <strong>{shortlist.length}</strong></li>
              </ul>
            </div>
          </aside>

          {/* Main */}
          <main className="md:col-span-9 space-y-10">
            {/* Recommended */}
            <section>
              <div className="flex items-end justify-between mb-5">
                <div>
                  <p className="overline flex items-center gap-2"><Sparkles className="h-3 w-3" /> AI Curated</p>
                  <h2 className="font-serif text-2xl text-brand-ink">Recommended for you</h2>
                </div>
                <Link to="/search" className="text-sm text-brand-maroon hover:underline">View all →</Link>
              </div>
              {recommended.length === 0 ? (
                <p className="text-sm text-brand-inkLight">Complete your profile to receive curated matches.</p>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="dashboard-recommended">
                  {recommended.slice(0, 4).map((p) => (
                    <ProfileCard
                      key={p.user_id}
                      profile={p}
                      compatibility={p.compatibility}
                      isShortlisted={!!shortlist.find((s) => s.user_id === p.user_id)}
                      onSendInterest={sendInterest}
                      onShortlist={toggleShortlist}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Interests received */}
            <section>
              <div className="flex items-end justify-between mb-5">
                <h2 className="font-serif text-2xl text-brand-ink">Interests received</h2>
              </div>
              {received.length === 0 ? (
                <p className="text-sm text-brand-inkLight">No new interests yet. Keep your profile updated and visible.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4" data-testid="dashboard-interests">
                  {received.map((it) => (
                    <div key={it.id} className="card-elegant p-5 flex items-center gap-4">
                      <Link to={`/profile/${it.from_user?.user_id}`}>
                        <img src={avatarUrl(it.from_user, 160)} alt="" className="w-20 h-20 rounded-full object-cover" />
                      </Link>
                      <div className="flex-1">
                        <h4 className="font-serif text-lg text-brand-ink">{it.from_user?.name}</h4>
                        <p className="text-xs text-brand-inkLight">{it.from_user?.profession || ""} {it.from_user?.location ? `· ${it.from_user.location}` : ""}</p>
                        <p className="text-[11px] text-brand-inkMute uppercase tracking-[0.15em] mt-1">Status: {it.status}</p>
                      </div>
                      {it.status === "pending" && (
                        <div className="flex flex-col gap-2">
                          <button onClick={() => respond(it.id, "accept")} className="text-xs bg-brand-maroon text-white px-3 py-1.5 rounded-md" data-testid={`accept-${it.id}`}>Accept</button>
                          <button onClick={() => respond(it.id, "reject")} className="text-xs border border-brand-line text-brand-inkLight px-3 py-1.5 rounded-md" data-testid={`reject-${it.id}`}>Decline</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Shortlist */}
            {shortlist.length > 0 && (
              <section>
                <h2 className="font-serif text-2xl text-brand-ink mb-5">Your shortlist</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                  {shortlist.slice(0, 4).map((p) => (
                    <ProfileCard
                      key={p.user_id}
                      profile={p}
                      isShortlisted
                      onSendInterest={sendInterest}
                      onShortlist={toggleShortlist}
                    />
                  ))}
                </div>
              </section>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
